/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/label-has-associated-control */

import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { selectValidPhotos } from '@redux/selectors/PhotoSelector';
import { updatePhotoDescription, setEditPhoto, softDeletePhoto } from '@redux/modules/photo';
import './Photo.css';
import Card from '@components/smartAdminUI/Card';
import photoPlaceholder from '@images/image-not-available.png';
import Edit from './Edit';
import Remove from './Remove';
import API from '../../../../../../config';

const Photo = forwardRef(
  ({
    item,
    value,
    // onEditClicked,
    // onRemove,
    listeners,
    renderItem,
    className,
    color,
    dragOverlay,
    dragging,
    disabled,
    fadeIn,
    handle,
    height,
    index,
    sorting,
    style,
    transition,
    transform,
    wrapperStyle,
    ...props
  }, ref) => {
    const dispatch = useDispatch();
    const name = item.id;

    const dynamicOrder = true; // governs whether the Order displayed in gridlist changes on drag, or stays the same

    const photoValidCollection = useSelector(selectValidPhotos);
    const photoMetaInstance = useMemo(() => photoValidCollection.find(photoMeta => photoMeta.key === item.key) ?? null, [item.key]);

    const [localFieldDescriptionValue, setLocalFieldValue] = useState('');

    useEffect(() => {
      if (photoMetaInstance.Description) setLocalFieldValue(photoMetaInstance.Description);
    }, [photoMetaInstance.Description]);

    const getThumbFromMeta = photoMeta => `${API.URL}Image/GetAwsFile?file=${photoMeta.thumbExternalURL}`;
    const image = useMemo(() => {
      if (item.thumbExternalURL) return getThumbFromMeta(photoMetaInstance);
      return null;
    }, [item.thumbExternalURL]);

    const isError = useMemo(() => {
      if (item?.IsError) return true;
      return null;
    }, [item.IsError]);

    useEffect(() => {
      if (!dragOverlay) {
        return undefined;
      }
      document.body.style.cursor = 'grabbing';
      return () => {
        document.body.style.cursor = '';
      };
    }, [dragOverlay]);

    const listStyle = {
      transition,
      '--translate-x': transform
        ? `${Math.round(transform.x)}px`
        : undefined,
      '--translate-y': transform
        ? `${Math.round(transform.y)}px`
        : undefined,
      '--scale-x': transform?.scaleX ? `${transform.scaleX}` : undefined,
      '--scale-y': transform?.scaleY ? `${transform.scaleY}` : undefined,
      '--index': index,
    };

    const handleBlur = e => {
      if (photoMetaInstance?.Description === e.target.value) return;
      photoMetaInstance.Description = e.target.value;
      dispatch(updatePhotoDescription(photoMetaInstance));
    };

    // pressing space in the description box selects the underlying card
    const handleSpaceAndEnter = e => {
      if (e.key === ' ' || e.code === 'Space' || e.keyCode === 32 || e.key === 'Enter') {
        e.preventDefault();
        setLocalFieldValue(`${localFieldDescriptionValue} `);
      }
    };

    const onRemove = () => {
      dispatch(softDeletePhoto(photoMetaInstance));
    };

    const onEditClicked = () => {
      dispatch(setEditPhoto(photoMetaInstance, photoPlaceholder));
    };

    const handleChange = e => {
      setLocalFieldValue(e.target.value);
    };

    useEffect(() => {
      if (photoMetaInstance?.Description == null || photoMetaInstance.Description === localFieldDescriptionValue) return;
      setLocalFieldValue(photoMetaInstance?.Description);
    }, [photoMetaInstance?.Description]);

    return (renderItem && item) ? (
      renderItem({
        dragOverlay: Boolean(dragOverlay),
        dragging: Boolean(dragging),
        sorting: Boolean(sorting),
        index,
        fadeIn: Boolean(fadeIn),
        listeners,
        ref,
        style,
        transform,
        transition,
        value,
      })
    ) : (
      <li
        ref={ref}
        className={classNames(
          className,
          'Wrapper',
          fadeIn && 'fadeIn',
          sorting && 'sorting',
          dragging && 'dragging',
          handle && 'withHandle',
          dragOverlay && 'dragOverlay',
          disabled && 'disabled',
        )}
        tabIndex={!handle ? 0 : undefined}
        {...(!handle ? listeners : undefined)}
        {...props}
        style={listStyle}
      >
        <Card
          header={(
            <div>
              <div className="float-left pt-1">
                {dynamicOrder
                  ? item?.SortOrder
                  : item.InitialSortOrder}
              </div>
              <div className="btn-group float-right" role="group" aria-label="Photo tools">
                {image ? (
                  <>
                    <Edit onEditClicked={onEditClicked} item={item} />
                    <Remove onRemove={onRemove} item={item} />
                  </>
                )
                  : (
                    <>
                      <Edit onEditClicked={onEditClicked} item={item} isDisabled />
                      <Remove onRemove={onRemove} item={item} />
                    </>
                  )}
              </div>
            </div>
          )}
        >
          {image
            ? (<img className="img-fluid photoItem" src={image} alt="" />)
            : isError ? (
              <i className="far fa-exclamation-circle fa-6x" />
            )
              : (
                <div className="d-flex justify-content-center">
                  <div className="spinner-border spinner-border-lg " role="status" aria-hidden="true">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              )}
          <div style={{ gridArea: 'descriptionBox' }} className="form-group">
            <input
              className="form-control descriptionBox"
              id={name}
              name={name}
              type="text"
              value={localFieldDescriptionValue}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyDown={handleSpaceAndEnter}
              placeholder="Description"
            />
          </div>
        </Card>
      </li>
    );
  },
);

Photo.propTypes = {
  isSelected: PropTypes.bool,
  className: PropTypes.string,
  color: PropTypes.string,
  dragOverlay: PropTypes.bool,
  dragging: PropTypes.bool,
  disabled: PropTypes.string,
  fadeIn: PropTypes.bool,
  height: PropTypes.string,
  listeners: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  onSelect: PropTypes.func,
  isDropped: PropTypes.bool,
  id: PropTypes.string,
  index: PropTypes.number,
  item: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  sorting: PropTypes.bool,
  style: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  transition: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  containerId: PropTypes.string,
  getIndex: PropTypes.func,
  handle: PropTypes.bool,
  renderItem: PropTypes.arrayOf(PropTypes.string, PropTypes.shape()),
  // onRemove: PropTypes.func,
  // onEditClicked: PropTypes.func,
  transform: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),
  wrapperStyle: PropTypes.oneOfType([PropTypes.shape(), PropTypes.array]),

};

export default Photo;
